import React, { useRef } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useSelector } from 'react-redux';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { BsFilePdfFill } from 'react-icons/bs';

function StackChart({ data, labels, colors, initialHiddenSeries, title }) {
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const chartRef = useRef(null);

    // Early return if no data
    if (!data || !labels || data.length === 0 || labels.length === 0) {
        return (
            <Box id="chart" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                لاتوجد بيانات
            </Box>
        );
    }

    const chartHeight = Math.max(500, labels.length * 85);

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = title || "Chart Report";

        const container = chartRef.current;
        if (!container) return;

        // Auto-scale down to fit a standard A4 page height (~950px in CSS pixels)
        const maxA4Height = 950;
        const originalTransform = container.style.transform;
        const originalTransformOrigin = container.style.transformOrigin;
        
        if (container.offsetHeight > maxA4Height) {
            const scale = maxA4Height / container.offsetHeight;
            container.style.transform = `scale(${scale})`;
            container.style.transformOrigin = 'top center';
        }

        // Add class to all ancestors to prevent them from being hidden, and reset their styles
        const ancestors = [];
        let p = container.parentNode;
        while (p && p.tagName !== 'BODY' && p.tagName !== 'HTML') {
            ancestors.push(p);
            p.classList.add('print-path');
            p = p.parentNode;
        }

        document.body.classList.add('printing-active');
        container.classList.add('print-this-element');

        window.print();

        // Cleanup
        document.body.classList.remove('printing-active');
        container.classList.remove('print-this-element');
        ancestors.forEach(el => el.classList.remove('print-path'));
        
        // Restore transform
        container.style.transform = originalTransform;
        container.style.transformOrigin = originalTransformOrigin;

        document.title = originalTitle;
    };

    const state = {
        series: data,
        options: {
            chart: {
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false,
                    }
                },
                type: 'bar',
                height: chartHeight,
                events: {
                    mounted: (chart) => {
                        if (initialHiddenSeries) {
                            initialHiddenSeries.forEach(name => {
                                chart.toggleSeries(name);
                            });
                        }
                    }
                }
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '70%',
                    dataLabels: {
                        position: 'top'
                    }
                },
            },
            dataLabels: {
                enabled: true,
                textAnchor: 'start',
                style: {
                    fontSize: '11px',
                    colors: ["#fff"],
                    fontFamily: 'Cairo-Bold'
                },
                formatter: function (val) {
                    return val > 0 ? val : '';
                },
                offsetX: 10,
                dropShadow: {
                    enabled: true,
                    top: 1,
                    left: 1,
                    blur: 1,
                    color: '#000',
                    opacity: 0.45
                }
            },
            stroke: {
                show: true,
                width: 1,
                colors: ['#fff']
            },
            title: {
                text: ''
            },
            xaxis: {
                categories: labels,
                axisBorder: {
                    show: false
                },
                axisTicks: {
                    show: false
                },
                title: {
                    text: 'الوزن (بالطن)',
                    style: {
                        fontFamily: 'Cairo-Medium'
                    }
                },
                labels: {
                    style: {
                        fontFamily: 'Cairo-Medium',
                        fontSize: '12px'
                    }
                }
            },
            yaxis: {
                axisBorder: {
                    show: false
                },
                axisTicks: {
                    show: false
                },
                labels: {
                    align: 'center',
                    minWidth: 200,
                    maxWidth: 600,
                    style: {
                        fontFamily: 'Cairo-Medium',
                        fontSize: '13px'
                    },
                }
            },
            grid: {
                padding: {
                    left: 20
                }
            },
            fill: {
                opacity: 1
            },
            legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'center',
                fontSize: '14px',
                fontFamily: 'Cairo-Medium',
                margin: 10
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                theme: 'light',
                style: {
                    fontSize: '12px',
                    fontFamily: 'Cairo-Medium'
                },
                y: {
                    formatter: function (val) {
                        return val + " طن"
                    }
                }
            },
            colors: colors || [maintheme?.primary, maintheme?.secondary, maintheme?.red || '#ff5252', maintheme?.yellow || '#c8b803'],
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        width: 200
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }]
        },
    };

    return (
        <Box 
            ref={chartRef}
            sx={{ 
                width: '100%', 
                height: 'auto', 
                position: 'relative',
                '@media print': {
                    '.pdf-export-button': {
                        display: 'none !important',
                    },
                    '.apexcharts-toolbar': {
                        display: 'none !important',
                    }
                }
            }}
        >
            <style>{`
                @media print {
                    html, body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        overflow: visible !important;
                        height: auto !important;
                        max-height: none !important;
                    }
                    body.printing-active *:not(.print-path):not(.print-this-element):not(.print-this-element *) {
                        display: none !important;
                    }
                    body.printing-active .print-path {
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        position: static !important;
                        width: 100% !important;
                        height: auto !important;
                        max-height: none !important;
                        overflow: visible !important;
                        transform: none !important;
                    }
                    body.printing-active .print-this-element {
                        display: block !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        height: auto !important;
                        max-height: none !important;
                        overflow: visible !important;
                    }
                }
            `}</style>

            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                px: 2,
                position: 'absolute',
                top: '-45px',
                width: '100%',
                zIndex: 10
            }}>
                <Tooltip title="طباعة كـ PDF">
                    <IconButton 
                        className="pdf-export-button"
                        onClick={handlePrint}
                        sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
                    >
                        <BsFilePdfFill size={24} />
                    </IconButton>
                </Tooltip>
                
                <Typography sx={{ 
                    fontFamily: 'Cairo-Bold', 
                    fontSize: '18px',
                    color: '#334155',
                    textAlign: 'right',
                    flex: 1
                }}>
                    {title}
                </Typography>
            </Box>

            <div id="chart">
                <ReactApexChart options={state.options} series={state.series} type="bar" height={state.options.chart.height} />
            </div>
        </Box>
    )
}

export default StackChart